<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Header del Modal -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            🚗
          </div>
          <div class="header-text">
            <h2>{{ $ml.get('lpr_event_details') }}</h2>
            <p class="plate-number">{{ eventData?.metadata?.plateNumber || 'N/A' }}</p>
          </div>
        </div>
        
        <!-- Navegación entre eventos LPR -->
        <div class="event-navigation">
          <button 
            class="nav-button"
            @click="navigateToPrevious"
            :disabled="!canNavigatePrevious"
            :title="$ml.get('previous_event') || 'Previous Event'"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          
          <span class="navigation-info">
            {{ currentIndex + 1 }} / {{ totalEvents }}
          </span>
          
          <button 
            class="nav-button"
            @click="navigateToNext"
            :disabled="!canNavigateNext"
            :title="$ml.get('next_event') || 'Next Event'"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
        
        <button class="close-button" @click="closeModal" :title="$ml.get('close')">
          <i class="fas fa-times" aria-hidden="true"></i>
          <span class="close-symbol" aria-hidden="true">×</span>
        </button>
      </div>

      <!-- Contenido del Modal -->
      <div class="modal-content">
        <!-- Información del Evento -->
        <div class="event-info-section">
          <h3>📋 {{ $ml.get('event_information') }}</h3>
          <div class="info-grid-compact">
            <div class="info-item">
              <label>{{ $ml.get('event_id') }}:</label>
              <span>{{ eventData?.id || 'N/A' }}</span>
            </div>
            <div class="info-item">
              <label>{{ $ml.get('plate') }}:</label>
              <span class="plate-value">{{ eventData?.metadata?.plateNumber || 'N/A' }}</span>
            </div>
            <div class="info-item">
              <label>{{ $ml.get('date') }}:</label>
              <span>{{ formatDate(eventData?.start) }}</span>
            </div>
            <div class="info-item">
              <label>{{ $ml.get('hour') }}:</label>
              <span>{{ formatTime(eventData?.start) }}</span>
            </div>
          </div>
        </div>

        <!-- Imagen de Evidencia -->
        <div class="evidence-section" v-if="eventData?.metadata?.evidence">
          <h3>📷 {{ $ml.get('evidence') }}</h3>
          <div class="evidence-container">
            <div v-if="imageLoading && !imageError" class="image-loading">
              <i class="fas fa-spinner fa-spin"></i>
              <p>{{ $ml.get('loading_image')}}</p>
            </div>
            <img 
              v-show="!imageLoading && !imageError"
              :src="getEvidenceUrl(eventData.metadata.evidence)" 
              :alt="`${$ml.get('evidence')} LPR - ${eventData.metadata.plateNumber}`"
              class="evidence-image"
              @load="handleImageLoad"
              @error="handleImageError"
              crossorigin="anonymous"
              referrerpolicy="no-referrer"
            />
            <div v-if="imageError" class="image-error">
              <i class="fas fa-exclamation-triangle"></i>
              <p>{{ $ml.get('image_load_error') }}</p>
              <button @click="retryImageLoad" class="retry-button">
                <i class="fas fa-redo"></i>
                Reintentar
              </button>
            </div>
          </div>
        </div>

        <!-- Video del Evento -->
        <div class="video-section">
          <h3>🎥 {{ $ml.get('video_event') }} </h3>
          <div class="video-container">
            <video 
              v-if="videoData && videoData.videoUrl"
              :src="videoData.videoUrl"
              controls
              class="event-video"
              @loadeddata="onVideoLoaded"
              @error="onVideoError"
            >
              {{ $ml.get('browser_not_support_video') }}
            </video>
            <div v-else-if="videoLoading" class="video-loading">
              <i class="fas fa-spinner fa-spin"></i>
              <p>{{ $ml.get('loading_video') }}</p>
            </div>
            <div v-else-if="videoError" class="video-error">
              <i class="fas fa-exclamation-triangle"></i>
              <p>{{ $ml.get('video_load_error') }}</p>
            </div>
            <div v-else class="no-video">
              <i class="fas fa-video-slash"></i>
              <p>{{ $ml.get('no_video_available') }}</p>
            </div>
          </div>
        </div>

      </div>

      <!-- Footer del Modal -->
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal">
          <i class="fas fa-times"></i>
          {{ $ml.get('close') }}
        </button>
        
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LPRAlertModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    eventData: {
      type: Object,
      default: null
    },
    // Navegación
    currentIndex: {
      type: Number,
      default: 0
    },
    totalEvents: {
      type: Number,
      default: 0
    },
    canNavigatePrevious: {
      type: Boolean,
      default: false
    },
    canNavigateNext: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      imageError: false,
      imageLoading: true,
      videoLoading: false,
      videoError: false,
      videoData: null
    }
  },
  watch: {
    eventData: {
      handler(newData) {
        if (newData && this.isVisible) {
          this.loadVideoForEvent();
        }
        // Reset image states when event data changes
        this.imageLoading = true;
        this.imageError = false;
        this.$nextTick(() => {
          this.debugImageElement();
        });
      },
      deep: true,
      immediate: true
    },
    isVisible(newValue) {
      if (newValue && this.eventData) {
        this.loadVideoForEvent();
        this.$nextTick(() => {
          this.debugImageElement();
        });
      } else {
        this.resetVideoData();
      }
    },
    imageLoading(newValue) {
      console.log('Image loading state changed:', newValue);
    },
    imageError(newValue) {
      console.log('Image error state changed:', newValue);
    }
  },
  mounted() {
    // Si el modal se monta con datos, buscar video
    if (this.eventData && this.isVisible) {
      this.loadVideoForEvent();
    }
    
    // Debug: verificar si el elemento img existe
    this.$nextTick(() => {
      this.debugImageElement();
    });
  },
  methods: {
    closeModal() {
      this.$emit('close');
      this.resetVideoData();
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      } catch (error) {
        return 'N/A';
      }
    },
    
    formatTime(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (error) {
        return 'N/A';
      }
    },
    
    formatDuration(duration) {
      if (!duration) return 'N/A';
      const seconds = Math.floor(duration / 1000);
      return `${seconds}s`;
    },
    
    getEvidenceUrl(evidence) {
      if (!evidence) {
        console.log('No evidence provided');
        return '';
      }
      
      let finalUrl = '';
      
      // Si la evidencia empieza con //, construir la URL completa
      if (evidence.startsWith('//')) {
        finalUrl = `${this.$URL}${evidence}`;
      }
      // Si ya es una URL completa, usarla directamente
      else if (evidence.startsWith('http')) {
        finalUrl = evidence;
      }
      // Si es una ruta relativa, construir la URL completa
      else {
        finalUrl = `${this.$URL}/${evidence}`;
      }
      
      console.log('Evidence URL constructed:', {
        original: evidence,
        baseURL: this.$URL,
        final: finalUrl
      });
      
      // Probar la URL antes de usarla
      this.testImageUrl(finalUrl);
      
      return finalUrl;
    },
    
    async testImageUrl(url) {
      try {
        console.log('Testing image URL:', url);
        const response = await fetch(url, { 
          method: 'HEAD',
          mode: 'no-cors' // Cambiar a no-cors para evitar OpaqueResponseBlocking
        });
        console.log('Image URL test result:', {
          url: url,
          status: response.status,
          statusText: response.statusText,
          type: response.type
        });
      } catch (error) {
        console.error('Image URL test failed:', {
          url: url,
          error: error.message,
          type: error.name
        });
      }
    },
    
    getFileName(filePath) {
      if (!filePath) return 'N/A';
      return filePath.split('/').pop();
    },
    
    handleImageLoad(event) {
      console.log('Image loaded successfully:', {
        src: event.target.src,
        alt: event.target.alt,
        naturalWidth: event.target.naturalWidth,
        naturalHeight: event.target.naturalHeight
      });
      this.imageError = false;
      this.imageLoading = false;
    },
    
    handleImageError(event) {
      console.error('Image load error:', {
        src: event.target.src,
        alt: event.target.alt,
        error: event
      });
      this.imageError = true;
      this.imageLoading = false;
    },
    
    retryImageLoad() {
      console.log('Retrying image load...');
      this.imageError = false;
      this.imageLoading = true;
      // Forzar recarga de la imagen
      this.$forceUpdate();
    },
    
    debugImageElement() {
      // Solo hacer debugging si el modal está visible y tiene datos
      if (!this.isVisible || !this.eventData) {
        console.log('Skipping debug - modal not visible or no event data');
        return;
      }
      
      console.log('Debugging image element...');
      console.log('Event data:', this.eventData);
      console.log('Evidence:', this.eventData?.metadata?.evidence);
      console.log('Image loading state:', this.imageLoading);
      console.log('Image error state:', this.imageError);
      
      // Verificar que $el existe antes de usar querySelector
      if (!this.$el || typeof this.$el.querySelector !== 'function') {
        console.log('$el not available or querySelector not supported');
        return;
      }
      
      // Buscar el elemento img en el DOM
      const imgElement = this.$el.querySelector('.evidence-image');
      console.log('Image element found:', imgElement);
      
      if (imgElement) {
        console.log('Image element details:', {
          src: imgElement.src,
          alt: imgElement.alt,
          complete: imgElement.complete,
          naturalWidth: imgElement.naturalWidth,
          naturalHeight: imgElement.naturalHeight,
          offsetWidth: imgElement.offsetWidth,
          offsetHeight: imgElement.offsetHeight,
          style: imgElement.style.cssText,
          classList: Array.from(imgElement.classList)
        });
        
        // Verificar si la imagen está visible
        const computedStyle = window.getComputedStyle(imgElement);
       
      } else {
        console.log('No image element found in DOM');
      }
    },
    
    async loadVideoForEvent() {
      if (!this.eventData || !this.eventData.metadata) {
        console.log('No event data available for video search');
        return;
      }
      
      
      
      this.videoLoading = true;
      this.videoError = false;
      this.videoData = null;
      
      try {
        // Buscar el video más cercano usando el timestamp del evento
        const timestamp = this.eventData.metadata.unixTimestamp || this.eventData.start;
        const deviceId = this.eventData.metadata.deviceId;
        
        
        
        if (timestamp && deviceId) {
          // Emitir evento para que el componente padre busque el video
          this.$emit('search-video', {
            timestamp: timestamp,
            deviceId: deviceId,
            eventData: this.eventData
          });
        } else {
          console.warn('Missing timestamp or deviceId for video search');
          this.videoError = true;
          this.videoLoading = false;
        }
      } catch (error) {
        console.error('Error loading video:', error);
        this.videoError = true;
        this.videoLoading = false;
      }
    },
    
    onVideoLoaded() {
      this.videoLoading = false;
      this.videoError = false;
    },
    
    onVideoError() {
      this.videoError = true;
      this.videoLoading = false;
    },
    
    resetVideoData() {
      this.videoData = null;
      this.videoLoading = false;
      this.videoError = false;
      this.imageError = false;
    },
    
    openVideoInMainPlayer() {
      if (this.videoData && this.videoData.videoUrl) {
        this.$emit('open-video-in-main-player', {
          videoData: this.videoData,
          eventData: this.eventData
        });
      }
    },
    
    // Método para recibir datos de video del componente padre
    setVideoData(videoData) {
      
      this.videoData = videoData;
      this.videoLoading = false;
      this.videoError = false;
      
      // Si no hay video data válido, mostrar estado de no disponible
      if (!videoData || !videoData.videoUrl) {
        this.videoData = null;
        this.videoError = false;
        this.videoLoading = false;
      }
    },

    // Métodos de navegación
    navigateToPrevious() {
      this.$emit('navigate-previous');
    },

    navigateToNext() {
      this.$emit('navigate-next');
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.modal-container {
  background: #282828;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid #404040;
  max-width: 800px;
  width: 90%;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: white;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #404040;
}

/* Estilos para la navegación de eventos */
.event-navigation {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 20px;
}

.nav-button {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.nav-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.nav-button i {
  font-size: 14px;
  color: white;
}

.navigation-info {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  min-width: 50px;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 8px;
  border-radius: 12px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-icon {
  font-size: 2.5rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.plate-number {
  margin: 5px 0 0 0;
  font-size: 1.2rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.2);
  padding: 5px 15px;
  border-radius: 20px;
  display: inline-block;
}

.close-button {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 16px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  position: relative;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.close-button:active {
  transform: scale(0.95);
}

.close-button i {
  font-size: 16px;
  line-height: 1;
  font-weight: bold;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  display: none; /* Ocultar FontAwesome para usar el símbolo Unicode */
}

.close-symbol {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 22px;
  font-weight: bold;
  color: white;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  display: block;
  font-family: Arial, sans-serif;
}

/* Asegurar que el botón sea visible en pantallas pequeñas */
@media (max-width: 768px) {
  .close-button {
    width: 48px;
    height: 48px;
    font-size: 18px;
  }
  
  .close-symbol {
    font-size: 24px;
  }
}

.modal-content {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  background: #282828;
  color: white;
}

.event-info-section,
.evidence-section,
.video-section {
  margin-bottom: 20px;
}

.event-info-section h3,
.evidence-section h3,
.video-section h3 {
  margin: 0 0 15px 0;
  color: white;
  font-size: 1.2rem;
  border-bottom: 2px solid #404040;
  padding-bottom: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
}

.info-grid-compact {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-item label {
  font-weight: 600;
  color: #b0b0b0;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-item span {
  color: white;
  font-size: 1rem;
  padding: 8px 12px;
  background: #404040;
  border-radius: 6px;
  border-left: 4px solid #1976d2;
}

.plate-value {
  font-weight: 700;
  font-size: 1.2rem;
  color: #1976d2 !important;
  background: rgba(25, 118, 210, 0.2) !important;
  border-left: 4px solid #1976d2 !important;
}

.event-type {
  background: rgba(76, 175, 80, 0.2) !important;
  color: #4caf50 !important;
  font-weight: 600;
  border-left: 4px solid #4caf50 !important;
}

.evidence-container {
  text-align: center;
  background: #404040;
  border-radius: 8px;
  padding: 20px;
  border: 2px dashed #666666;
}

.evidence-image {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s;
}

.evidence-image:hover {
  transform: scale(1.02);
}

.image-loading {
  color: #1976d2;
  padding: 40px;
  text-align: center;
  background: rgba(25, 118, 210, 0.1);
  border-radius: 8px;
  border: 1px dashed #1976d2;
}

.image-loading i {
  font-size: 2rem;
  margin-bottom: 15px;
  display: block;
}

.image-error {
  color: #f44336;
  padding: 20px;
  background: rgba(244, 67, 54, 0.1);
  border-radius: 8px;
  border: 1px dashed #f44336;
  text-align: center;
}

.image-error i {
  font-size: 2rem;
  margin-bottom: 10px;
  display: block;
}

.retry-button {
  background: #f44336;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: background 0.2s;
}

.retry-button:hover {
  background: #d32f2f;
}

.video-container {
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.event-video {
  width: 100%;
  height: auto;
  max-height: 500px;
}

.video-loading,
.video-error,
.no-video {
  color: white;
  text-align: center;
  padding: 40px;
}

.video-loading i,
.video-error i,
.no-video i {
  font-size: 3rem;
  margin-bottom: 15px;
  display: block;
}

.video-loading i {
  color: #1976d2;
}

.video-error i {
  color: #dc3545;
}

.no-video i {
  color: #6c757d;
}

.additional-info {
  background: #404040;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #666666;
}

.file-path {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  word-break: break-all;
  background: #333333 !important;
  color: #b0b0b0 !important;
  border-left: 4px solid #666666 !important;
}

.modal-footer {
  background: #404040;
  padding: 20px 30px;
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  border-top: 1px solid #666666;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  text-decoration: none;
}

.btn-secondary {
  background: #666666;
  color: white;
  border: 1px solid #888888;
}

.btn-secondary:hover {
  background: #777777;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.btn-primary {
  background: #1976d2;
  color: white;
  border: 1px solid #1565c0;
}

.btn-primary:hover {
  background: #1565c0;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.btn i {
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 10px;
  }
  
  .modal-content {
    padding: 15px;
  }
  
  .info-grid,
  .info-grid-compact {
    grid-template-columns: 1fr;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 1024px) {
  .info-grid-compact {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
